import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { API, api } from '@unboared/react';

import { getRawSocket } from '~/utils/getRawSocket';

/**
 * Hook that records Socket.IO lifecycle events as Sentry breadcrumbs.
 * When an error occurs later, Sentry shows the trail of connection events
 * that led to the error — making it possible to diagnose disconnection issues.
 *
 * Must be called after UnboaredGame has initialized the API.
 */
export function useSentrySocketBreadcrumbs() {
    useEffect(() => {
        const apiInstance = api();
        if (!apiInstance) return undefined;

        // Access the raw Socket.IO client instance.
        const wss = getRawSocket();
        if (!wss) return undefined;

        const cleanups: (() => void)[] = [];

        // --- Raw Socket.IO manager events ---
        const manager = wss.io;
        if (manager) {
            const onReconnectAttempt = (attempt: number) => {
                Sentry.addBreadcrumb({
                    category: 'socket.io',
                    message: `Reconnect attempt #${attempt}`,
                    level: 'warning',
                });
            };

            const onReconnect = (attempt: number) => {
                Sentry.addBreadcrumb({
                    category: 'socket.io',
                    message: `Reconnected after ${attempt} attempts`,
                    level: 'info',
                });
            };

            const onReconnectFailed = () => {
                Sentry.addBreadcrumb({
                    category: 'socket.io',
                    message: 'Reconnect failed (all attempts exhausted)',
                    level: 'error',
                });
                Sentry.captureMessage('Socket.IO reconnection failed permanently', 'error');
            };

            const onError = (err: Error) => {
                Sentry.addBreadcrumb({
                    category: 'socket.io',
                    message: `Transport error: ${err.message}`,
                    level: 'error',
                });
            };

            manager.on('reconnect_attempt', onReconnectAttempt);
            manager.on('reconnect', onReconnect);
            manager.on('reconnect_failed', onReconnectFailed);
            manager.on('error', onError);

            cleanups.push(() => {
                manager.off('reconnect_attempt', onReconnectAttempt);
                manager.off('reconnect', onReconnect);
                manager.off('reconnect_failed', onReconnectFailed);
                manager.off('error', onError);
            });
        }

        // --- Unboared-level events ---
        const unsubConnect = API.onConnect((deviceID: string) => {
            Sentry.addBreadcrumb({
                category: 'unboared',
                message: `Device connected: ${deviceID}`,
                level: 'info',
                data: { deviceID, isSelf: deviceID === API.getDeviceID() },
            });
        });
        cleanups.push(unsubConnect);

        const unsubDisconnect = API.onDisconnect((deviceID: string) => {
            const isSelf = deviceID === API.getDeviceID();
            Sentry.addBreadcrumb({
                category: 'unboared',
                message: `Device disconnected: ${deviceID}`,
                level: isSelf ? 'error' : 'warning',
                data: { deviceID, isSelf },
            });
            if (isSelf) {
                Sentry.captureMessage('Own device disconnected from session', 'warning');
            }
        });
        cleanups.push(unsubDisconnect);

        const onTempDisconnect = (apiInstance as any).onTemporaryDisconnect?.(() => {
            Sentry.addBreadcrumb({
                category: 'unboared',
                message: 'Temporary disconnection (grace period started)',
                level: 'warning',
            });
        });
        if (onTempDisconnect) {
            cleanups.push(onTempDisconnect);
        }

        return () => {
            cleanups.forEach((cleanup) => cleanup());
        };
    }, []);
}
